import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Storefront, CurrencyInr, Tag, Receipt, Plus, Percent, TrendUp, Handshake, Phone, Globe, MapPin, NavigationArrow, CaretDown, CaretUp
} from "@phosphor-icons/react";

export default function StorePage() {
  const { token, user, isSuperAdmin } = useAuth();
  const [stores, setStores] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [savings, setSavings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [txnDialogOpen, setTxnDialogOpen] = useState(false);
  const [myPartnerStore, setMyPartnerStore] = useState(null);
  const [partnerStores, setPartnerStores] = useState([]);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [expandedTnc, setExpandedTnc] = useState({});
  const [applyForm, setApplyForm] = useState({ store_name: "", store_description: "", store_address: "", store_phone: "", store_category: "", discount_percentage: "", discount_fixed: "" });

  const [storeForm, setStoreForm] = useState({ name: "", description: "", discount_percentage: "", category: "", address: "" });
  const [txnForm, setTxnForm] = useState({ store_id: "", bill_amount: "" });

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [storesRes, txnRes, savingsRes, myStoreRes, partnerStoresRes] = await Promise.all([
        axios.get(`${API}/stores`, { headers }),
        axios.get(`${API}/stores/transactions/my`, { headers }),
        axios.get(`${API}/stores/savings/summary`, { headers }),
        axios.get(`${API}/partner-stores/my-store`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/partner-stores`, { headers }).catch(() => ({ data: [] }))
      ]);
      setStores(storesRes.data);
      setTransactions(txnRes.data);
      setSavings(savingsRes.data);
      setMyPartnerStore(myStoreRes.data);
      setPartnerStores(partnerStoresRes.data || []);
    } catch (err) {
      console.error("Store data fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  usePolling(fetchData, 10000);

  const handleCreateStore = async () => {
    if (!storeForm.name || !storeForm.discount_percentage) {
      toast.error("Store name and discount are required");
      return;
    }
    try {
      await axios.post(`${API}/stores`, {
        ...storeForm,
        discount_percentage: parseFloat(storeForm.discount_percentage)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Store added!");
      setStoreDialogOpen(false);
      setStoreForm({ name: "", description: "", discount_percentage: "", category: "", address: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create store");
    }
  };

  const handleRecordTransaction = async () => {
    if (!txnForm.store_id || !txnForm.bill_amount) {
      toast.error("Please select a store and enter bill amount");
      return;
    }
    const store = stores.find(s => s.id === txnForm.store_id);
    const billAmt = parseFloat(txnForm.bill_amount);
    const discountAmt = Math.round(billAmt * (store?.discount_percentage || 0) / 100 * 100) / 100;

    try {
      await axios.post(`${API}/stores/transaction`, {
        store_id: txnForm.store_id,
        member_id: user.id,
        bill_amount: billAmt,
        discount_amount: discountAmt
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Saved ${discountAmt.toLocaleString("en-IN", { style: "currency", currency: "INR" })}!`);
      setTxnDialogOpen(false);
      setTxnForm({ store_id: "", bill_amount: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record transaction");
    }
  };

  const selectedStore = stores.find(s => s.id === txnForm.store_id);
  const previewDiscount = txnForm.bill_amount && selectedStore
    ? Math.round(parseFloat(txnForm.bill_amount) * selectedStore.discount_percentage / 100 * 100) / 100
    : 0;

  const handleApplyPartnerStore = async () => {
    if (!applyForm.store_name || !applyForm.store_address || !applyForm.store_category) {
      toast.error("Store name, address, and category are required"); return;
    }
    try {
      await axios.post(`${API}/partner-stores/apply`, {
        ...applyForm,
        discount_percentage: applyForm.discount_percentage ? parseFloat(applyForm.discount_percentage) : null,
        discount_fixed: applyForm.discount_fixed ? parseFloat(applyForm.discount_fixed) : null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Partner store application submitted!");
      setShowApplyDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to apply"); }
  };

  return (
    <>
      <SEO title="Stores" description="Discover partner stores, track savings, and manage discount transactions." path="/stores" noIndex />
      <div className="space-y-6 animate-fadeInUp" data-testid="store-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-3xl md:text-4xl text-primary">Partner Stores</h1>
          <div className="flex gap-2">
            {isSuperAdmin && (
            <Dialog open={txnDialogOpen} onOpenChange={setTxnDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-bch-gold hover:bg-bch-gold-dark text-black" data-testid="record-bill-btn">
                  <Receipt className="w-5 h-5 mr-2" /> Record Bill
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-[var(--border-color)] max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-primary text-xl">Record Store Bill</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-secondary">Select Store *</Label>
                    <Select value={txnForm.store_id} onValueChange={v => setTxnForm(p => ({ ...p, store_id: v }))}>
                      <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary">
                        <SelectValue placeholder="Choose store" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-[var(--border-color)]">
                        {stores.map(s => (
                          <SelectItem key={s.id} value={s.id} className="text-primary">{s.name} ({s.discount_percentage}% off)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-secondary">Bill Amount (₹) *</Label>
                    <Input
                      type="number"
                      value={txnForm.bill_amount}
                      onChange={e => setTxnForm(p => ({ ...p, bill_amount: e.target.value }))}
                      className="bg-surface border-[var(--border-color)] text-primary"
                      placeholder="Enter bill amount"
                      data-testid="bill-amount-input"
                    />
                  </div>
                  {previewDiscount > 0 && (
                    <div className="p-3 bg-bch-teal/10 border border-bch-teal/20 rounded-lg text-center">
                      <p className="text-secondary text-sm">You save</p>
                      <p className="text-bch-teal font-mono text-2xl font-bold" data-testid="discount-preview">
                        ₹{previewDiscount.toLocaleString("en-IN")}
                      </p>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button onClick={handleRecordTransaction} className="bg-bch-gold hover:bg-bch-gold-dark text-black font-medium">
                    Save Transaction
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}

            {isSuperAdmin && (
              <Dialog open={storeDialogOpen} onOpenChange={setStoreDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-bch-teal hover:bg-bch-teal-dark" data-testid="add-store-btn">
                    <Plus className="w-5 h-5 mr-2" /> Add Store
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-[var(--border-color)] max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-primary text-xl">Add Partner Store</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label className="text-secondary">Store Name *</Label>
                      <Input value={storeForm.name} onChange={e => setStoreForm(p => ({ ...p, name: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="Store name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-secondary">Discount Percentage *</Label>
                      <Input type="number" value={storeForm.discount_percentage} onChange={e => setStoreForm(p => ({ ...p, discount_percentage: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="e.g. 10" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-secondary">Category</Label>
                      <Input value={storeForm.category} onChange={e => setStoreForm(p => ({ ...p, category: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="e.g. Restaurant" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-secondary">Address</Label>
                      <Input value={storeForm.address} onChange={e => setStoreForm(p => ({ ...p, address: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="Store address" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-secondary">Description</Label>
                      <Input value={storeForm.description} onChange={e => setStoreForm(p => ({ ...p, description: e.target.value }))} className="bg-surface border-[var(--border-color)] text-primary" placeholder="Brief description" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={handleCreateStore} className="bg-bch-teal hover:bg-bch-teal-dark font-medium">Add Store</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Savings Summary */}
        {savings && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="savings-summary">
            <div className="bg-sky-50 border border-[var(--border-color)] rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                  <CurrencyInr className="w-5 h-5 text-white" weight="fill" />
                </div>
                <span className="text-secondary text-sm">Total Spent</span>
              </div>
              <p className="font-mono text-2xl text-primary font-bold" data-testid="total-spent">
                ₹{savings.total_spent.toLocaleString("en-IN")}
              </p>
            </div>
            <div className="bg-emerald-50 border border-[var(--border-color)] rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                  <TrendUp className="w-5 h-5 text-white" weight="fill" />
                </div>
                <span className="text-secondary text-sm">Total Saved</span>
              </div>
              <p className="font-mono text-2xl text-amber-500 font-bold" data-testid="total-saved">
                ₹{savings.total_saved.toLocaleString("en-IN")}
              </p>
            </div>
            <div className="bg-amber-50 border border-[var(--border-color)] rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Receipt className="w-5 h-5 text-white" weight="fill" />
                </div>
                <span className="text-secondary text-sm">Transactions</span>
              </div>
              <p className="font-mono text-2xl text-primary font-bold" data-testid="txn-count">
                {savings.transaction_count}
              </p>
            </div>
          </div>
        )}

        {/* My Partner Store Section */}
        {!myPartnerStore ? (
          <div className="bg-gradient-to-r from-bch-gold/5 to-bch-teal/5 border border-bch-gold/20 rounded-2xl p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Handshake className="w-8 h-8 text-bch-gold" weight="duotone" />
              <div>
                <p className="text-primary font-semibold">Become a Partner Store</p>
                <p className="text-muted text-xs">Offer exclusive discounts to BCH members and grow your business</p>
              </div>
            </div>
            <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
              <DialogTrigger asChild>
                <Button className="bg-bch-gold hover:bg-bch-gold/90 text-black" data-testid="become-partner-btn">Apply Now</Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-[var(--border-color)] max-w-md">
                <DialogHeader><DialogTitle className="text-primary text-xl">Apply as Partner Store</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <Input required placeholder="Store Name *" value={applyForm.store_name} onChange={e => setApplyForm({...applyForm, store_name: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" data-testid="apply-store-name" />
                  <Input required placeholder="Store Category *" value={applyForm.store_category} onChange={e => setApplyForm({...applyForm, store_category: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" />
                  <Input required placeholder="Store Address *" value={applyForm.store_address} onChange={e => setApplyForm({...applyForm, store_address: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" />
                  <Input placeholder="Store Phone" value={applyForm.store_phone} onChange={e => setApplyForm({...applyForm, store_phone: e.target.value.replace(/\D/g, "").slice(0, 10)})} className="bg-surface border-[var(--border-color)] text-primary" />
                  <Input placeholder="Description" value={applyForm.store_description} onChange={e => setApplyForm({...applyForm, store_description: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" />
                  <div className="grid grid-cols-2 gap-3">
                    <Input type="number" placeholder="Discount %" value={applyForm.discount_percentage} onChange={e => setApplyForm({...applyForm, discount_percentage: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" />
                    <Input type="number" placeholder="Fixed Discount" value={applyForm.discount_fixed} onChange={e => setApplyForm({...applyForm, discount_fixed: e.target.value})} className="bg-surface border-[var(--border-color)] text-primary" />
                  </div>
                  <Button onClick={handleApplyPartnerStore} className="w-full bg-bch-gold hover:bg-bch-gold/90 text-black font-bold" data-testid="submit-partner-apply">Submit Application</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <div className="bg-card border border-[var(--border-color)] rounded-2xl p-5" data-testid="my-partner-store">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Storefront className="w-6 h-6 text-bch-gold" weight="duotone" />
                <h3 className="text-primary font-semibold">My Partner Store: {myPartnerStore.store_name}</h3>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${myPartnerStore.status === "approved" ? "bg-green-500/10 text-green-500" : myPartnerStore.status === "pending" ? "bg-amber-500/10 text-amber-500" : "bg-red-500/10 text-red-500"}`}>{myPartnerStore.status}</span>
              </div>
              {myPartnerStore.status === "approved" && (
                <a href="/partner-dashboard"><Button size="sm" className="bg-bch-teal hover:bg-bch-teal/90 text-white" data-testid="manage-store-btn">Manage Store</Button></a>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><p className="text-muted text-xs">Transactions</p><p className="text-primary font-bold">{myPartnerStore.total_transactions}</p></div>
              <div><p className="text-muted text-xs">Total Discount Given</p><p className="text-primary font-bold">{myPartnerStore.total_discount_given?.toFixed(2) || "0.00"}</p></div>
              <div><p className="text-muted text-xs">Revenue</p><p className="text-primary font-bold">{myPartnerStore.total_revenue?.toFixed(2) || "0.00"}</p></div>
            </div>
          </div>
        )}

        {/* Partner Stores Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
          </div>
        ) : partnerStores.length === 0 ? (
          <div className="text-center py-20 bg-card border border-[var(--border-color)] rounded-lg">
            <Storefront className="w-16 h-16 text-muted mx-auto mb-4" weight="duotone" />
            <p className="text-secondary">No partner stores yet</p>
          </div>
        ) : (
          <>
            <h2 className="font-display text-xl text-primary">Featured Partner Stores</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="partner-stores-grid">
              {partnerStores.map(store => (
                <div key={store.id} className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-bch-gold/30 group" data-testid={`partner-card-${store.id}`}>
                  {/* Cover Photo */}
                  <div className="relative h-40 bg-gradient-to-br from-bch-gold/20 via-bch-teal/10 to-bch-gold/5 overflow-hidden">
                    {store.cover_url ? (
                      <img src={store.cover_url} alt={store.store_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Storefront className="w-16 h-16 text-bch-gold/30" weight="duotone" />
                      </div>
                    )}
                    {/* Logo overlay */}
                    <div className="absolute bottom-3 left-3">
                      {store.logo_url ? (
                        <img src={store.logo_url} alt="logo" className="w-12 h-12 rounded-xl bg-white object-cover shadow-lg border-2 border-white" />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-white/90 backdrop-blur flex items-center justify-center shadow-lg border-2 border-white">
                          <span className="text-bch-gold font-bold text-lg">{store.store_name?.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                    {/* Discount Badge */}
                    {(store.discount_percentage || store.discount_fixed) && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 bg-rose-500 text-white rounded-lg text-xs font-bold shadow-lg">
                        {store.discount_percentage ? `${store.discount_percentage}% OFF` : `₹${store.discount_fixed} OFF`}
                      </div>
                    )}
                  </div>
                  {/* Card Body */}
                  <div className="p-4">
                    <h3 className="text-primary font-semibold text-base truncate">{store.store_name}</h3>
                    <p className="text-muted text-xs mt-0.5">{store.owner_name}</p>
                    {store.store_description && <p className="text-secondary text-xs mt-2 line-clamp-2">{store.store_description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <span className="px-2 py-0.5 bg-bch-gold/10 text-bch-gold text-[10px] font-semibold rounded-full">{store.store_category}</span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--border-color)]">
                      {store.google_maps_link ? (
                        <a href={store.google_maps_link} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg text-xs font-semibold hover:bg-blue-500/20 transition-colors" data-testid={`directions-${store.id}`}>
                          <NavigationArrow className="w-3.5 h-3.5" weight="bold" /> Directions
                        </a>
                      ) : (
                        <span className="flex items-center gap-1.5 px-3 py-1.5 bg-surface text-muted rounded-lg text-xs">
                          <NavigationArrow className="w-3.5 h-3.5" weight="bold" /> Directions
                        </span>
                      )}
                      {store.store_phone && (
                        <a href={`tel:${store.store_phone}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-bch-teal/10 text-bch-teal rounded-lg text-xs font-semibold hover:bg-bch-teal/20 transition-colors" data-testid={`call-${store.id}`}>
                          <Phone className="w-3.5 h-3.5" weight="bold" /> Call
                        </a>
                      )}
                      {store.website && (
                        <a href={store.website.startsWith("http") ? store.website : `https://${store.website}`} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 rounded-lg bg-surface hover:bg-purple-500/10 transition-colors" title="Website">
                          <Globe className="w-3.5 h-3.5 text-muted hover:text-purple-500" weight="bold" />
                        </a>
                      )}
                    </div>

                    {/* Terms & Conditions */}
                    <div className="mt-3">
                      <button onClick={() => setExpandedTnc(prev => ({ ...prev, [store.id]: !prev[store.id] }))}
                        className="flex items-center justify-between w-full text-left text-muted text-[11px] font-semibold uppercase tracking-wider hover:text-primary transition-colors py-1"
                        data-testid={`tnc-toggle-${store.id}`}>
                        <span>Terms & Conditions</span>
                        {expandedTnc[store.id] ? <CaretUp className="w-3.5 h-3.5" weight="bold" /> : <CaretDown className="w-3.5 h-3.5" weight="bold" />}
                      </button>
                      {expandedTnc[store.id] && (
                        <ul className="mt-1.5 space-y-1 text-[11px] text-secondary pl-3 list-disc" data-testid={`tnc-list-${store.id}`}>
                          <li>Discount applicable only for BCH members with valid membership</li>
                          <li>Must present BCH digital ID before billing</li>
                          <li>{store.discount_percentage ? `${store.discount_percentage}% discount` : ""}{store.discount_percentage && store.discount_fixed ? " or " : ""}{store.discount_fixed ? `Flat ₹${store.discount_fixed} off` : ""} on total bill</li>
                          <li>Cannot be combined with other offers or promotions</li>
                          <li>Store reserves the right to modify terms with prior notice</li>
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Recent Transactions */}
        {transactions.length > 0 && (
          <div data-testid="recent-transactions">
            <h2 className="font-display text-xl text-primary mb-4">Recent Transactions</h2>
            <div className="bg-card border border-[var(--border-color)] rounded-lg divide-y divide-[var(--border-color)]">
              {transactions.slice(0, 10).map(txn => (
                <div key={txn.id} className="flex items-center justify-between p-4">
                  <div>
                    <p className="text-primary font-medium">{txn.store_name}</p>
                    <p className="text-muted text-xs">{new Date(txn.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-secondary font-mono">₹{txn.bill_amount?.toLocaleString("en-IN")}</p>
                    <p className="text-bch-teal text-xs font-medium">Saved ₹{txn.discount_amount?.toLocaleString("en-IN")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
